/**
 * @fileoverview Vuex getters for maker-checker computed state.
 *
 * @module store/modules/makerchecker/getters
 *
 * Provides computed values for:
 * - User authentication and department permissions
 * - Filtered pending and completed changes
 * - Formatted department names
 * - Maker-checker module configuration
 */

import { GetterTree } from "vuex/types";
import { RootState } from "@/store/types";
import { MakerCheckerState } from "./types";
import _ from "lodash";
import { getDepartmentVerifiedChanges } from "@/helperMethods/makerChecker";

const getters: GetterTree<MakerCheckerState, RootState> = {
  /**
   * Gets user authentication data with normalized departments.
   *
   * @returns User metadata with lowercase department names including 'general'
   */
  getUserAuthentication(state, getters, rootState) {
    const userMetaData = _.assign({}, state.userAuthentication);
    userMetaData.departments = userMetaData.departments
      ? userMetaData.departments.map((d) => d.toLowerCase())
      : [];
    if (!userMetaData.departments.includes("general")) {
      userMetaData.departments.push("general");
    }
    return userMetaData;
  },
  makerCheckerObject(state, getters, rootState) {
    return _.assign(_.get(rootState, "modules.makerChecker", {}), {});
  },
  /**
   * Checks if maker-checker workflow is enabled.
   *
   * @returns Boolean indicating if maker-checker is enabled
   */
  makerCheckerEnabled(state, getters, rootState) {
    const makerCheckerEnabled = _.get(rootState, "modules.makerChecker.enabled", false);
    return makerCheckerEnabled;
  },
  /**
   * Gets list of user's departments (normalized and including 'general').
   *
   * @returns Array of lowercase department names
   */
  departments(state, getters, rootState) {
    const departments = _.get(state, "userAuthentication.departments", []);

    const editedDepartments = _.map(_.cloneDeep(departments), (v) => v && v.toLowerCase());
    !editedDepartments.includes("general") && editedDepartments.push("general");

    return editedDepartments ? editedDepartments : [];
    // return departments ? [] : []; // Testing of authentication
  },
  /**
   * Gets formatted department names (capitalized).
   *
   * @returns Array of capitalized department names for display
   */
  formattedDepartments(state, getters, rootState) {
    const departments = getters.departments;
    let formattedDepartments: string[] = [];
    if (departments) {
      formattedDepartments = _.map(departments, (dept) => {
        return dept
          .split(" ")
          .map((word: string) => _.capitalize(word))
          .join(" ")
          .trim();
      });
    }
    return _.uniq(formattedDepartments);
  },
  /**
   * Gets pending changes filtered by user's department permissions.
   *
   * @returns Array of pending changes user has permission to view
   */
  pendingChangesList(state, getters, rootState) {
    const filtered = getDepartmentVerifiedChanges(state.pendingChanges, getters.departments);
    return filtered;
  },
  /**
   * Gets completed changes filtered by user's department permissions.
   *
   * @returns Array of completed changes user has permission to view
   */
  completedChangesList(state, getters, rootState) {
    const filtered = getDepartmentVerifiedChanges(state.completedChanges, getters.departments);
    return filtered;
  },

  showAdvanced(state, getters, rootState) {
    return rootState.showAdvanced;
  },
  mainType(state, getters, rootState) {
    return state.mainType;
  },
  department(state, getters, rootState) {
    return state.currentDepartment;
  },
};

export default getters;
